import * as React from 'react'
import { useMutation } from '@tanstack/react-query'
import { Check } from 'lucide-react'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { postJSON } from '@/lib/api'

export type VerifiedEventBuyer = {
  token: string
  buyer: { name: string; email: string }
}

type Redemption = { verification: VerifiedEventBuyer }

export function MemberTicketVerification({
  eventId,
  token,
  sessionVerified = false,
  onVerified,
}: {
  eventId: number
  token: string | null
  sessionVerified?: boolean
  onVerified: (verification: VerifiedEventBuyer) => void
}) {
  const [isPromptOpen, setIsPromptOpen] = React.useState(false)
  const [email, setEmail] = React.useState('')
  const [code, setCode] = React.useState('')
  const request = useMutation({
    mutationFn: () =>
      postJSON(`/api/v1/public/events/${eventId}/member_verification_request`, {
        member_verification: { email },
      }),
  })
  const redemption = useMutation({
    mutationFn: () =>
      postJSON<Redemption>(`/api/v1/public/events/${eventId}/member_verification_redemption`, {
        member_verification: { email, code },
      }),
    onSuccess: ({ verification }) => onVerified(verification),
  })

  if (token || sessionVerified)
    return (
      <div className="flex items-center gap-2 border-b border-border bg-primary/5 px-4 py-3">
        <Check className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
        <span className="text-caption text-muted-foreground">Verified member</span>
      </div>
    )

  if (!isPromptOpen) {
    return (
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
        <span className="text-caption text-muted-foreground">
          Member-only tickets require verification
        </span>
        <Button
          type="button"
          variant="outline"
          className="h-8 px-3"
          onClick={() => setIsPromptOpen(true)}
        >
          Verify email
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-2 border-b border-border px-4 py-3">
      <div className="flex gap-2">
        <Input
          aria-label="Member email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          className="h-8 min-w-0 flex-1 bg-background"
        />
        <Button
          type="button"
          variant="outline"
          className="h-8 px-3"
          disabled={!email || request.isPending}
          onClick={() => request.mutate()}
        >
          Send code
        </Button>
      </div>
      {request.isSuccess && (
        <p className="text-caption text-muted-foreground">We emailed a 6-digit code to {email}</p>
      )}
      {request.isSuccess && (
        <div className="flex gap-2">
          <Input
            aria-label="6-digit verification code"
            inputMode="numeric"
            maxLength={6}
            value={code}
            onChange={(event) => setCode(event.target.value)}
            className="h-8 min-w-0 flex-1 bg-background tabular-nums"
          />
          <Button
            type="button"
            variant="outline"
            className="h-8 px-3"
            disabled={!code || redemption.isPending}
            onClick={() => redemption.mutate()}
          >
            Confirm
          </Button>
        </div>
      )}
      {redemption.isError && <Alert>The code is invalid or expired</Alert>}
    </div>
  )
}
